import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import styles from "@/components/CustomComponents/CampaignForm/index.module.scss";
import {
  verifyForm,
  landingpageservice,
} from "@/services/formSubmitAPI/client";
import { renderField } from "@/components/CustomComponents/CampaignForm/renderField";
import { useRouter } from "next/router";
import {
  useContentfulTranslations,
  useApacConfigurations,
} from "@/utils/contentfulTranslations";
import { openPopup } from "@/utils/helper";
import { formValidation } from "@/components/CustomComponents/CampaignForm/formValidation";
import { LoadingIcon } from "@/components/CustomComponents/CampaignForm/icon";
import { useLocale } from "@/hooks/useLocale";
import { capitalizeFirstLetter } from "@/utils/formatters";

const CustomErrorMessage = ({ name, errors, touched }) => {
  if (!touched[name] || !errors[name]) return null;

  return <p className="mt-1 text-sm text-red-600">{errors[name]}</p>;
};

export default function CampaignForm({ appointyData }) {
  const [apiEnd, setApiEnd] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [payload, setPayload] = useState({});
  const router = useRouter();
  const locale = useLocale();
  const [submitError, setSubmitError] = useState(null);
  const { formLink = "" } = appointyData || {};
  const [selected, setSelected] = useState(0); // For radiobutton
  const { formData = {} } = useContentfulTranslations();
  const { items: formConfigData = [] } = useApacConfigurations();
  const countryForm = `${capitalizeFirstLetter(process.env.NEXT_PUBLIC_COUNTRY_NAME)} - Forms`;
  const getFormData = formLink => {
    const result = formLink.replaceAll("/", "");
    return formData?.[result]?.[locale] || formData?.[result]?.en || {};
  };

  const formsDataSection = getFormData(formLink);
  const { formFields = [] } = formsDataSection || {};
  const initialValues = formFields?.reduce((acc, field) => {
    acc[field.name] =
      field.defaultValue ?? field?.radioButtonOptions?.[0] ?? "";
    return acc;
  }, {});

  const formConfigurations =
    formConfigData?.[0]?.fields?.configurations.filter(
      config => config?.fields?.internalTitle === countryForm
    ) || [];

  const emailConfig = formConfigurations?.[0]?.fields?.config;

  const [formTemplate, storeRecipient] =
    Object.entries(emailConfig ?? {}).find(([key]) => key === formLink) || [];

  const {
    url = "",
    privacyPolicy = "",
    api = "",
    submit = "Submit",
    submitting = "Submitting",
    selectList = "Select",
    landingPage = "/promotion-thankyou",
    capchaErrorMessage = "Please verify the captcha",
    capchaVerificationMessage = "Captcha verification failed",
    tryAgainMessage = "Something went wrong. Please try again.",
  } = formsDataSection;

  useEffect(() => {
    setApiEnd(formsDataSection?.api);
    setButtonUrl(formsDataSection?.buttonUrl);
  }, [formsDataSection]);

  const validationSchema = formValidation(formFields);
  useEffect(() => {
    if (!apiEnd || !payload || Object.keys(payload).length === 0) return;
    const submitData = async () => {
      try {
        const res = await landingpageservice({
          values: payload,
          api: apiEnd,
        });
        if (res?.message) {
          router.push(landingPage);
        }
      } catch (err) {
        console.error(err);
        setSubmitError(tryAgainMessage);
      }
    };

    if (apiEnd && Object.keys(payload).length > 0) {
      submitData();
    }
  }, [apiEnd, payload]);

  async function handleSubmit(
    values,
    { setSubmitting, setFieldError, resetForm }
  ) {
    setSubmitError(null);
    try {
      // Step 1: Check captcha token exists
      if (!values.captchaToken) {
        setFieldError("captchaToken", capchaErrorMessage);
        setSubmitting(false);
        return;
      }

      // Step 2: Verify captcha with server
      const verifyData = await verifyForm(values.captchaToken);

      if (verifyData && verifyData?.data?.success) {
        // Step 3: Send only known field names (exclude captcha etc.)
        const fieldNames = new Set(formFields.map(f => f.name));

        const payloads = {
          api: apiEnd,
          ...(buttonUrl != null && { buttonLink: buttonUrl }),
          storeRecipient,
          formTemplate,
          ...Object.fromEntries(
            Object.entries(values).filter(([key]) => fieldNames.has(key))
          ),
        };
        setPayload(payloads);
      } else {
        setSubmitting(false);
        resetForm();
        return;
      }
    } catch (err) {
      console.error(err);
      setSubmitError(tryAgainMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className="mx-auto max-w-[980px] px-4 py-[50px]">
        {formsDataSection?.heading ? (
          <>
            <h2 className="text-center text-[45px] md:text-[60px] mb-4 font-[Helvetica-Light,_Helvetica,_sans-serif] text-[#232323]">
              {formsDataSection?.heading}
            </h2>
            {formsDataSection?.showLine && (
              <hr className="mt-3 mb-6 border-t border-black-300" />
            )}
          </>
        ) : null}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, isSubmitting }) => (
            <Form className="space-y-5 w-4/5 mx-auto " noValidate>
              {formFields?.map(field => (
                <div
                  key={field.name}
                  className="grid md:grid-cols-12 gap-4 form-element"
                >
                  {field.type !== "checkbox" ? (
                    <label htmlFor={field?.label} className="md:col-span-6">
                      {field.label?.toLowerCase() !== "captcha" && (
                        <>
                          {field.label}
                          {field?.required && (
                            <span
                              aria-hidden="true"
                              className="ml-1 text-red-500"
                            >
                              *
                            </span>
                          )}
                        </>
                      )}
                    </label>
                  ) : (
                    ""
                  )}

                  <div
                    className={
                      field.type === "checkbox"
                        ? "md:col-span-12"
                        : "md:col-span-6"
                    }
                  >
                    {renderField(
                      setFieldValue,
                      field,
                      errors[field.name],
                      touched[field.name],
                      setSelected,
                      selected
                    )}

                    <CustomErrorMessage
                      name={field.name}
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>
              ))}

              {submitError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="text-red-600 text-sm text-center"
                >
                  {submitError}
                </div>
              )}

              <div className="md:grid w-full ">
                <div className="col-span-3 col-start-6 text-center">
                  {url && privacyPolicy && (
                    <a
                      href={url}
                      rel="noopener noreferrer"
                      className="block mb-3 text-sm text-[#337ab7]  text-left !font-[HelveticaRoman,Helvetica,sans-serif]"
                      onClick={e => openPopup(e, url)}
                    >
                      {privacyPolicy}
                    </a>
                  )}
                </div>

                <div className="col-span-3 col-start-6  text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center h-10 w-full cursor-pointer bg-[#3E4A75] text-white py-2 text-[13px] font-[HelveticaBold,Helvetica,sans-serif] uppercase rounded disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting && <LoadingIcon />}
                    {isSubmitting ? submitting : submit}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
